import {
  TellescopeModelFromName,
  useTellescopeInfiniteQuery,
  useTellescopeQuery,
  useUser,
} from "@tellescope/tanstack-query";
import {
  Button,
  DialogContent,
  DialogFooter,
  DialogHeader,
  PaginatedList,
  SearchInput,
  Spinner,
  useDebouncedValue,
} from "@tellescope/ui";
import { sanitizeEmailHTMLContent } from "@tellescope/utils";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export interface InboxTemplateSelectorDialogContentsProps {
  contextFilters?: {
    channels?: TellescopeModelFromName<"templates">["forChannels"];
    enduserOnly?: boolean;
    userId?: string;
    enduserId?: string;
  };
  onSelectTemplate: (template: TellescopeModelFromName<"templates">) => void;
  onCancel: () => void;
  enableQuery?: boolean;
}

export const InboxTemplateSelectorDialogContents = ({
  contextFilters,
  onSelectTemplate,
  onCancel,
  enableQuery = true,
}: InboxTemplateSelectorDialogContentsProps) => {
  const { t } = useTranslation("messaging");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery);

  const user = useUser(contextFilters?.userId ?? "", !!contextFilters?.userId);
  const enduser = useTellescopeQuery({
    model: "endusers",
    method: "getOne",
    request: contextFilters?.enduserId ?? "",
    enabled: !!contextFilters?.enduserId,
  });

  const templatesQuery = useTellescopeInfiniteQuery({
    model: "templates",
    method: "getSome",
    request: {
      search: debouncedSearchQuery
        ? {
            query: debouncedSearchQuery,
          }
        : undefined,
      mdbFilter: {
        type: contextFilters?.enduserOnly ? { $in: ["enduser"] } : { $in: ["enduser", "team"] },
        hideFromCompose: { $nin: [true] },
        forRoles: user?.roles?.length ? { $in: [...user.roles, null] } : undefined,
        forChannels: contextFilters?.channels?.length ? { $in: [...contextFilters.channels, null] } : undefined,
        forEntityTypes: enduser.data?.customTypeId ? { $in: [enduser.data.customTypeId, null] } : undefined,
        forUserTags: user?.tags?.length ? { $in: [...user.tags, null] } : undefined,
      },
    },
    enabled: enableQuery,
    select: (data) => data.pages.flat(),
  });

  return (
    <DialogContent>
      <DialogHeader>{t("Message Templates")}</DialogHeader>
      <div className="space-y-4">
        <div className="w-full pr-3">
          <SearchInput
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("Search templates...")}
            className="w-full"
          />
        </div>
        <PaginatedList
          onBottomReached={templatesQuery.fetchNextPage}
          className="h-[500px] max-h-[80svh] overflow-y-auto"
          hideScrollbarUntilScroll
        >
          {templatesQuery.isLoading || searchQuery !== debouncedSearchQuery ? (
            <div className="flex flex-col items-center justify-center gap-2 h-[500px] max-h-[80svh]">
              <Spinner />
              {t("Loading...")}
            </div>
          ) : templatesQuery.data?.length ? (
            templatesQuery.data.map((template) => (
              <button
                key={template.id}
                className="px-4 py-2 border rounded-md space-y-2 hover:bg-gray-50 w-full mb-2 text-left"
                onClick={() => onSelectTemplate(template)}
              >
                <p className="text-md font-semibold">{template.title}</p>
                {template.message && !template.html && (
                  <p className="text-muted-foreground text-sm line-clamp-2 truncate">{template.message}</p>
                )}
                {template.html && (
                  <div
                    className="line-clamp-2 truncate prose opacity-80"
                    dangerouslySetInnerHTML={{ __html: sanitizeEmailHTMLContent(template.html) }}
                  />
                )}
              </button>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center gap-2 h-[500px] max-h-[80svh] text-sm text-muted-foreground">
              {t("No templates to display")}
            </div>
          )}
        </PaginatedList>
      </div>
      <DialogFooter className="flex justify-end">
        <Button variant="outline" onClick={onCancel}>
          {t("Cancel")}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};
